/**
 * UI primitives — token-driven, RTL-safe (logical properties only),
 * a11y-first. Deliberately small: the design system stays in CSS tokens.
 * Icons come exclusively from components/icons.tsx — never emoji.
 */
import { forwardRef, useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { ChevronDown, X } from './icons';

// ── Button ───────────────────────────────────────────────────────────────────

type ButtonVariant = 'accent' | 'surface' | 'ghost' | 'danger' | 'promo';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  accent:
    'bg-accent text-accent-contrast hover:bg-accent-strong font-semibold shadow-[0_0_28px_-10px_var(--accent)]',
  surface: 'bg-surface-2 text-text hover:bg-surface-3 border border-border hover:border-border-strong',
  ghost: 'bg-transparent text-muted hover:bg-surface-2 hover:text-text',
  danger: 'bg-negative/15 text-negative hover:bg-negative/25 border border-negative/30',
  promo: 'bg-promo text-white hover:opacity-90 font-semibold',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'surface', size = 'md', loading, className = '', children, disabled, ...rest },
  ref,
) {
  const sizes = {
    sm: 'px-3.5 py-1.5 text-[13px] rounded-full',
    md: 'px-4.5 py-2.5 text-sm rounded-full',
    lg: 'px-6 py-3 text-[15px] rounded-full',
  };
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex select-none items-center justify-center gap-2 whitespace-nowrap transition-[background,border-color,transform,box-shadow] duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${BUTTON_STYLES[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
});

/** Icon-only button — aria-label is mandatory by type. */
export function IconButton({
  label,
  children,
  className = '',
  active = false,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`inline-flex size-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-2 hover:text-text focus-visible:outline-2 focus-visible:outline-accent ${
        active ? 'bg-surface-2 text-text' : ''
      } ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Spinner({ className = 'size-5' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-20" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ── Badges & chips ───────────────────────────────────────────────────────────

export function Badge({ children, tone = 'accent' }: { children: ReactNode; tone?: 'accent' | 'promo' | 'muted' | 'positive' | 'premium' }) {
  const tones = {
    accent: 'bg-accent text-accent-contrast',
    promo: 'bg-promo text-white',
    muted: 'bg-surface-3 text-muted',
    positive: 'bg-positive/15 text-positive',
    premium: 'bg-premium/15 text-premium',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function Chip({ children, active = false, onClick }: { children: ReactNode; active?: boolean; onClick?: () => void }) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
        active ? 'border-accent bg-accent/10 text-accent' : 'border-border bg-surface-2 text-muted hover:border-border-strong hover:text-text'
      } ${onClick ? 'cursor-pointer' : ''}`}
    >
      {children}
    </Tag>
  );
}

// ── Form controls ────────────────────────────────────────────────────────────

const FIELD =
  'w-full rounded-xl border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-text placeholder:text-muted transition-[border-color,box-shadow] hover:border-border-strong focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className = '', ...rest },
  ref,
) {
  return <input ref={ref} className={`${FIELD} ${className}`} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className = '', ...rest },
  ref,
) {
  return <textarea ref={ref} className={`${FIELD} resize-none ${className}`} {...rest} />;
});

export function Select({ className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select className={`${FIELD} appearance-none pe-9 ${className}`} {...rest}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
        checked ? 'bg-accent' : 'bg-surface-3'
      }`}
    >
      <span
        className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-all ${
          checked ? 'start-[calc(100%-1.375rem)]' : 'start-0.5'
        }`}
      />
    </button>
  );
}

/** Pill-group selector (durations, aspects, resolutions). */
export function PillGroup<T extends string | number>({
  options,
  value,
  onChange,
  format,
}: {
  options: readonly T[];
  value: T | undefined;
  onChange: (v: T) => void;
  format?: (v: T) => string;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup">
      {options.map((opt) => (
        <button
          key={String(opt)}
          type="button"
          role="radio"
          aria-checked={value === opt}
          onClick={() => onChange(opt)}
          className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
            value === opt
              ? 'border-accent bg-accent font-semibold text-accent-contrast'
              : 'border-border bg-surface-2 text-muted hover:border-border-strong hover:text-text'
          }`}
        >
          {format ? format(opt) : String(opt)}
        </button>
      ))}
    </div>
  );
}

// ── Layout bits ──────────────────────────────────────────────────────────────

export function Card({ children, className = '', interactive = false }: { children: ReactNode; className?: string; interactive?: boolean }) {
  return <div className={`rounded-2xl border border-border bg-surface ${interactive ? 'card-hover' : ''} ${className}`}>{children}</div>;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, children, wide = false }: { open: boolean; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Keep the latest onClose without re-running the open/close effect.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // A11y contract while open: Escape closes, focus moves in (and is trapped),
  // body scroll is locked, and focus returns to the opener on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0]!; // length checked above
      const last = focusables[focusables.length - 1]!;
      const current = document.activeElement;
      if (current instanceof Node && !panel.contains(current)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (current === first || current === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative max-h-[88dvh] w-full overflow-y-auto rounded-3xl border border-border bg-surface shadow-2xl outline-none ${wide ? 'max-w-4xl' : 'max-w-lg'}`}
        style={{ animation: 'scale-in 0.18s cubic-bezier(0.21, 1.02, 0.73, 1) both' }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute end-3 top-3 z-10 flex size-8 items-center justify-center rounded-full bg-surface-2 text-muted transition-colors hover:bg-surface-3 hover:text-text"
        >
          <X className="size-4" />
        </button>
        {children}
      </div>
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className}`} aria-hidden />;
}

/** Icon slot takes a component from icons.tsx (e.g. <Film />), never emoji. */
export function EmptyState({ icon, title, action }: { icon: ReactNode; title: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border py-16 text-center">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-surface-2 text-muted [&>svg]:size-6" aria-hidden>
        {icon}
      </div>
      <p className="text-sm text-muted">{title}</p>
      {action}
    </div>
  );
}
